import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { buscarEquipoPorReferencia, API_BASE, getFileUrl } from '../api';

import { FiArrowLeft, FiCalendar, FiClock, FiTool, FiFileText, FiDownload, FiMapPin, FiInfo } from 'react-icons/fi';
import moment from 'moment';
import './EquipoDetalle.css';

const EquipoDetalle = ({ usuario }) => {
  const { ref } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [equipo, setEquipo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await buscarEquipoPorReferencia(ref);
        setEquipo(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching equipment:', err);
        setError('No se pudo encontrar el equipo con esa referencia.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ref]);

  if (loading) return (
    <div className="equipo-detalle-status">
      <div className="loader"></div>
      <p>Cargando datos del equipo...</p>
    </div>
  );

  if (error) return (
    <div className="equipo-detalle-status error">
      <FiInfo size={48} />
      <h2>{error}</h2>
      <Link to="/" className="botonAzul"><FiArrowLeft /> Volver al Inicio</Link>
    </div>
  );

  const { tipo, datos, mantenimientos, archivos } = equipo;

  return (
    <div className="equipo-detalle-wrapper">
      <header className="detalle-header-top">
        <Link to="/" className="back-btn"><FiArrowLeft /> Volver al panel</Link>
        <div className="header-info">
          <div className="title-area">
            <h1>{datos.nombre}</h1>
            <span className={`badge-tipo ${tipo}`}>{tipo.toUpperCase()}</span>
          </div>
          <div className="ref-badge">REF: {datos.referencia_interna}</div>
        </div>
      </header>

      <div className="detalle-content-grid">
        {/* Lado Izquierdo: Info Técnica */}
        <div className="content-left">
          <section className="detalle-section main-info">
            <div className="section-header">
              <h3><FiInfo /> Información Técnica</h3>
            </div>
            
            <div className="info-body">
              {datos.imagen && (
                <div className="hero-image">
                  <img src={getFileUrl(datos.imagen)} alt={datos.nombre} />

                </div>
              )}
              
              <div className="technical-list">
                <div className="tech-item">
                  <span className="label">Matrícula / SN</span>
                  <span className="value">{datos.matricula}</span>
                </div>
                <div className="tech-item">
                  <span className="label">Categoría</span>
                  <span className="value">{datos.tipo}</span>
                </div>
                
                {tipo === 'vehiculo' ? (
                  <>
                    <div className="tech-item highlight">
                      <span className="label">Kilometraje Actual</span>
                      <span className="value"><FiMapPin /> {datos.km_actual?.toLocaleString()} km</span>
                    </div>
                    <div className="tech-item">
                      <span className="label">Próxima ITV</span>
                      <span className={`value ${moment(datos.fecha_proxima_itv).isBefore(moment()) ? 'overdue' : ''}`}>
                        <FiCalendar /> {datos.fecha_proxima_itv ? moment(datos.fecha_proxima_itv).format('DD/MM/YYYY') : 'No asignada'}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="tech-item highlight">
                      <span className="label">Horas Totales</span>
                      <span className="value"><FiClock /> {datos.horas_actuales?.toLocaleString()} h</span>
                    </div>
                    {datos.fecha_proxima_itv && (
                      <div className="tech-item">
                        <span className="label">Próxima Revisión</span>
                        <span className={`value ${moment(datos.fecha_proxima_itv).isBefore(moment()) ? 'overdue' : ''}`}>
                          <FiCalendar /> {moment(datos.fecha_proxima_itv).format('DD/MM/YYYY')}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          <section className="detalle-section documents-section">
            <div className="section-header">
              <h3><FiFileText /> Documentación</h3>
            </div>
            <div className="docs-grid">
              {archivos && archivos.length > 0 ? (
                archivos.map(archivo => (
                  <a 
                    key={archivo.id} 
                    href={getFileUrl(archivo.ruta)} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    download
                    className="doc-link"
                  >

                    <div className="doc-icon"><FiFileText /></div>
                    <div className="doc-meta">
                      <span className="name">{archivo.nombre}</span>
                    </div>
                    <FiDownload className="dl" />
                  </a>
                ))
              ) : (
                <p className="empty-msg">No hay documentos cargados para este equipo.</p>
              )}
            </div>
          </section>
        </div>

        {/* Lado Derecho: Historial */}
        <div className="content-right">
          <section className="detalle-section history-section">
            <div className="section-header">
              <h3><FiTool /> Historial de Mantenimientos</h3>
            </div>
            
            <div className="timeline-container">
              {mantenimientos && mantenimientos.length > 0 ? (
                mantenimientos.sort((a,b) => moment(b.fecha) - moment(a.fecha)).map(m => (
                  <div key={m.id} className="timeline-block">
                    <div className="timeline-marker"></div>
                    <div className="timeline-card">
                      <div className="card-header">
                        <span className="date">{moment(m.fecha).format('DD/MM/YYYY')}</span>
                        {m.km_actual && <span className="stat"><FiMapPin /> {m.km_actual} km</span>}
                        {m.horas_actuales && <span className="stat"><FiClock /> {m.horas_actuales} h</span>}
                      </div>
                      <div className="card-body">
                        <h4>{m.descripcion}</h4>
                        {m.observaciones && <p className="obs">{m.observaciones}</p>}
                        
                        {m.detalles && m.detalles.length > 0 && (
                          <div className="materials-used">
                            <h5>Materiales:</h5>
                            <div className="tags">
                              {m.detalles.map(d => (
                                <span key={d.id} className="tag">
                                  {d.cantidad_usada}x {d.nombre} <small>({d.referencia})</small>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-history">
                   <FiTool size={32} />
                   <p>Este equipo no tiene mantenimientos registrados todavía.</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default EquipoDetalle;
